import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { FileText, Lightbulb, Eye } from "lucide-react";
import { fetchExamplesFromBackend } from "../../lib/exampleStorage";
import useRetryFetch from "../../hooks/useRetryFetch";
import { toast } from "sonner";
import ExamplePreviewDialog from "../examples/ExamplePreviewDialog";
import UserProfileModal from "./UserProfileModal";
import ExampleDetailModal from "./ExampleDetailModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

interface MarketplaceExamplesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  activityName: string;
}

/**
 * Modal for viewing published examples in marketplace
 * Read-only mode - users can only view examples, no edit/delete/publish actions
 */
const MarketplaceExamplesModal = ({
  open,
  onOpenChange,
  workflowId,
  activityName,
}: MarketplaceExamplesModalProps) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [examples, setExamples] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewExample, setPreviewExample] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedExampleDetail, setSelectedExampleDetail] = useState<any>(null);

  const loadingRef = useRef(false);

  // Function to refresh examples list (only published)
  const refreshExamples = async () => {
    if (!workflowId || loadingRef.current) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      const loadedExamples = await fetchExamplesFromBackend(
        workflowId,
        retryFetch
      );

      // Filter to show only published examples
      const publishedExamples = loadedExamples.filter(
        (ex: any) => ex.isPublished
      );
      setExamples(publishedExamples);
    } catch (error) {
      console.error("Failed to load examples:", error);
      toast.error(t("failed_to_load_examples") || "Failed to load examples");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Load examples when modal opens
  useEffect(() => {
    if (open && workflowId) {
      refreshExamples();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workflowId]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <Lightbulb className="w-5 h-5 text-purple-500" />
                  {t("examples") || "Examples"}
                </DialogTitle>
                <DialogDescription className="mt-2">
                  {t("view_published_examples_for") ||
                    "View published examples for"}{" "}
                  <span className="font-medium text-gray-900">
                    {activityName}
                  </span>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="px-6 py-4 max-h-[calc(85vh-140px)]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4">
                  <div className="w-8 h-8 border-4 border-primary rounded-full border-t-transparent animate-spin"></div>
                </div>
                <p className="text-sm text-gray-500">
                  {t("loading") || "Loading..."}
                </p>
              </div>
            ) : examples.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4 bg-gray-100 rounded-full">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  {t("no_examples_available") || "No examples available"}
                </h3>
                <p className="max-w-sm text-sm text-gray-500">
                  {t("no_published_examples_description") ||
                    "There are no published examples for this activity yet. Check back later!"}
                </p>
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[35%]">
                        {t("example_name") || "Example Name"}
                      </TableHead>
                      <TableHead className="w-[25%]">
                        {t("created_by") || "Created By"}
                      </TableHead>
                      <TableHead className="w-[15%] text-right">
                        {t("actions") || "Actions"}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {examples.map((example) => (
                      <TableRow key={example._id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedExampleDetail(example);
                              }}
                              className="text-sm text-left cursor-pointer text-primary hover:underline"
                            >
                              {example.name}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {example.isCreatorAnonymous ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex flex-col gap-0.5 text-left p-1 w-full">
                                    <span className="text-sm font-medium text-gray-600">
                                      {t("anonymous") || "Anonymous"}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">
                                    {t("anonymous_creator_tooltip") ||
                                      "The creator of this example chose to publish it anonymously."}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedUser(example.creator);
                              }}
                              className="flex flex-col gap-0.5 text-left hover:bg-gray-50 p-1 rounded transition-colors w-full"
                            >
                              <span className="text-sm font-medium cursor-pointer text-primary hover:underline">
                                {example.creator?.name || "Unknown"}
                              </span>
                              {example.creator?.email && (
                                <span className="text-xs text-gray-500">
                                  {example.creator.email}
                                </span>
                              )}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => setPreviewExample(example)}
                            className="h-8 gap-1.5"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span className="text-xs">{t("view")}</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex items-center justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("close") || "Close"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Example Preview Dialog */}
      <ExamplePreviewDialog
        example={previewExample}
        activityId={workflowId}
        open={previewExample !== null}
        onOpenChange={(open: any) => !open && setPreviewExample(null)}
        isEditMode={false}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        open={selectedUser !== null}
        onOpenChange={(open) => !open && setSelectedUser(null)}
        user={selectedUser}
      />

      {/* Example Detail Modal */}
      <ExampleDetailModal
        open={selectedExampleDetail !== null}
        onOpenChange={(open) => !open && setSelectedExampleDetail(null)}
        example={
          selectedExampleDetail
            ? {
                name: selectedExampleDetail.name,
                description: selectedExampleDetail.description,
                prologue: selectedExampleDetail.prologue,
                epilogue: selectedExampleDetail.epilogue,
              }
            : null
        }
        onViewClick={() => {
          setPreviewExample(selectedExampleDetail);
          setSelectedExampleDetail(null);
        }}
      />
    </>
  );
};

export default MarketplaceExamplesModal;
